import { useCustom } from '@refinedev/core';
import { ArrowRight, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatMoney, formatRelative } from '@/lib/format';
import type { GiftcardSummary, PurchaseQueue, QueueGame } from '@/lib/types';
import { SelectAccountDialog } from './select-account-dialog';

// A API pode devolver o objeto direto ou embrulhado em { data } — normaliza.
type QueueResponse = PurchaseQueue | { data: PurchaseQueue };

function unwrapQueue(res: QueueResponse | undefined): PurchaseQueue | null {
  if (!res) return null;
  if ('urgent' in res) return res;
  if ('data' in res && res.data && 'urgent' in res.data) return res.data;
  return null;
}

// grupo → demanda desc → preço asc (UX-SPEC §3)
function sortGames(items: QueueGame[]): QueueGame[] {
  return [...items].sort((a, b) => {
    const da = a.demand ?? a.wishers ?? 0;
    const db = b.demand ?? b.wishers ?? 0;
    if (db !== da) return db - da;
    return (Number(a.our_price_brl) || 0) - (Number(b.our_price_brl) || 0);
  });
}

export function PurchaseQueuePage() {
  const navigate = useNavigate();
  const [buyTarget, setBuyTarget] = useState<QueueGame | null>(null);

  const { data, isLoading, isError, refetch, isFetching } = useCustom<QueueResponse>({
    url: '/purchase-queue',
    method: 'get',
  });

  const queue = unwrapQueue(data?.data);
  const urgent = sortGames(queue?.urgent ?? []);
  const onDemand = sortGames(queue?.on_demand ?? []);
  const wishlist = queue?.wishlist ?? [];
  const giftcards = queue?.giftcards ?? [];

  const isEmpty =
    !isLoading && !isError && urgent.length === 0 && onDemand.length === 0 && wishlist.length === 0;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Fila de Compras</h1>
          <p className="text-muted-foreground">
            {queue
              ? `${urgent.length} urgente${urgent.length === 1 ? '' : 's'} · ${onDemand.length} sob encomenda · ${wishlist.length} na wishlist`
              : 'Jogos aguardando compra nas contas doadoras.'}
          </p>
        </div>
        <Button variant="outline" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={isFetching ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
          Atualizar
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-28 w-full" />
          ))}
        </div>
      ) : isError ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
            <p className="text-sm text-muted-foreground">
              Não foi possível carregar a fila de compras.
            </p>
            <Button variant="outline" onClick={() => refetch()}>
              Tentar de novo
            </Button>
          </CardContent>
        </Card>
      ) : isEmpty ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
            <p className="text-lg font-medium">Nada na fila. Estoque em dia.</p>
            <Button variant="link" asChild>
              <Link to="/contas">Ver contas PSN</Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {urgent.length > 0 && (
            <QueueGroup
              title="🔴 Pedidos pagos — comprar agora"
              items={urgent}
              giftcards={giftcards}
              urgent
              onBuy={setBuyTarget}
              onSeeGiftcards={() => navigate('/topup')}
            />
          )}

          {onDemand.length > 0 && (
            <QueueGroup
              title="🟡 Sob encomenda com demanda"
              items={onDemand}
              giftcards={giftcards}
              onBuy={setBuyTarget}
              onSeeGiftcards={() => navigate('/topup')}
            />
          )}

          {wishlist.length > 0 && (
            <section className="space-y-3">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                ⚪ Wishlist
              </h2>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Jogo</TableHead>
                      <TableHead className="w-40">Na wishlist</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {wishlist.map((g) => (
                      <TableRow key={`${g.product_ref ?? g.game_title}`}>
                        <TableCell className="font-medium">{g.game_title}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {g.wishers ?? 0} {(g.wishers ?? 0) === 1 ? 'pessoa' : 'pessoas'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </section>
          )}
        </>
      )}

      <SelectAccountDialog
        open={buyTarget !== null}
        onOpenChange={(o) => !o && setBuyTarget(null)}
        game={buyTarget}
      />
    </div>
  );
}

function QueueGroup({
  title,
  items,
  giftcards,
  urgent,
  onBuy,
  onSeeGiftcards,
}: {
  title: string;
  items: QueueGame[];
  giftcards: GiftcardSummary[];
  urgent?: boolean;
  onBuy: (g: QueueGame) => void;
  onSeeGiftcards: () => void;
}) {
  const totalGiftcards = giftcards.reduce((acc, g) => acc + g.available_count, 0);
  const giftcardSummary = giftcards
    .filter((g) => g.available_count > 0)
    .map((g) => formatMoney(g.total_face_value, g.currency))
    .join(' · ');

  return (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h2>
      <div className="space-y-3">
        {items.map((g) => {
          const demand = g.demand ?? g.wishers ?? 0;
          const detailParts = [
            g.our_price_brl != null ? formatMoney(g.our_price_brl, 'BRL') : null,
            g.access_count ? `rende ${g.access_count} acessos` : null,
            // demand conta ITENS (pedido com 2 unidades = 2), não pedidos.
            urgent
              ? `${demand} ${demand === 1 ? 'item pago aguardando' : 'itens pagos aguardando'}`
              : `${demand} interessado${demand === 1 ? '' : 's'}`,
            urgent && g.oldest_paid_at ? `mais antigo ${formatRelative(g.oldest_paid_at)}` : null,
          ].filter(Boolean);

          return (
            <Card key={`${g.product_ref ?? g.game_title}`}>
              <CardContent className="flex flex-wrap items-center justify-between gap-4 py-4">
                <div className="min-w-0 space-y-1.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-base font-semibold">{g.game_title}</span>
                    {urgent && <Badge variant="destructive">Urgente</Badge>}
                    {g.cross_gen && <Badge variant="outline">Cross-gen</Badge>}
                  </div>
                  <p className="text-sm text-muted-foreground">{detailParts.join(' · ')}</p>
                  {totalGiftcards > 0 ? (
                    <Badge variant="success">
                      {totalGiftcards} gift card{totalGiftcards === 1 ? '' : 's'} disponíve
                      {totalGiftcards === 1 ? 'l' : 'is'} ({giftcardSummary})
                    </Badge>
                  ) : (
                    <Badge variant="destructive">Sem gift card — comprar primeiro</Badge>
                  )}
                </div>
                {totalGiftcards > 0 ? (
                  <Button size="lg" onClick={() => onBuy(g)}>
                    Comprar
                    <ArrowRight />
                  </Button>
                ) : (
                  <Button size="lg" variant="outline" onClick={onSeeGiftcards}>
                    Ver gift cards
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </section>
  );
}
